import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { curriculumService, type Syllabus } from "../services/curriculumService";
import { studentService } from "../services/studentService";
import { usePermissions } from "../hooks/usePermissions";
import { apiErrorMessage } from "../utils/apiError";
import { ClientRegister, StatusDot, type RegisterColumn } from "../components/register/ClientRegister";
import { Notice, type NoticeState } from "../components/register/RecordParts";
import { SyllabusForm } from "../components/syllabus/SyllabusForms";
import { SyllabusWorkspace } from "../components/syllabus/SyllabusWorkspace";
import { activeStatus, levelRank, syllabusConfig, withPlanCounts, type SyllabusRow } from "../components/syllabus/syllabusLogic";

const columns: RegisterColumn<SyllabusRow>[] = [
  { header: "Syllabus", cell: (row) => <span className="font-medium text-gray-900">{row.title}</span>, exportValue: (row) => row.title, className: "min-w-[14rem] whitespace-normal" },
  { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "min-w-[8rem] whitespace-normal text-gray-700" },
  { header: "Class level", cell: (row) => row.grade_name, exportValue: (row) => row.grade_name, className: "text-gray-700" },
  { header: "Topics", cell: (row) => (row.topicCount ? row.topicCount : row.is_active ? <span className="text-amber-700">None</span> : 0), exportValue: (row) => row.topicCount, align: "right" },
  { header: "Weeks", cell: (row) => row.weeks || <span className="text-gray-300">—</span>, exportValue: (row) => row.weeks, align: "right" },
  { header: "Lesson plans", cell: (row) => row.lessonCount, exportValue: (row) => row.lessonCount, align: "right" },
  {
    header: "Covered",
    cell: (row) => (row.coverage === null ? <span className="text-gray-300">Not recorded</span> : `${row.coverage}%`),
    exportValue: (row) => (row.coverage === null ? "" : row.coverage),
    align: "right",
  },
  { header: "Status", cell: (row) => <StatusDot {...activeStatus(row)} />, exportValue: (row) => activeStatus(row).label },
];

/** Every syllabus the school teaches from, and each one's teaching plan. */
export default function SyllabusManagement() {
  const { hasCapability } = usePermissions();
  const canLead = hasCapability("access.curriculum.leadership");
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const openId = Number(params.get("syllabus")) || null;
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<NoticeState>(null);

  const syllabi = useQuery({ queryKey: ["syllabi", "all"], queryFn: curriculumService.getAllSyllabi });
  const subjects = useQuery({ queryKey: ["curriculum-subjects"], queryFn: () => curriculumService.getSubjects() });
  const classes = useQuery({ queryKey: ["classes", "syllabus-levels"], queryFn: () => studentService.getClasses({ ordering: "level" }) });
  const topics = useQuery({ queryKey: ["curriculum-topics"], queryFn: curriculumService.getTopics });
  const lessons = useQuery({ queryKey: ["curriculum-lessons"], queryFn: curriculumService.getLessons });
  const progress = useQuery({ queryKey: ["curriculum-progress"], queryFn: curriculumService.getProgress });
  const refresh = () =>
    Promise.all(["syllabi", "syllabus-usage", "curriculum-topics", "curriculum-lessons", "curriculum-progress", "curriculum-stats"].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));

  const rows = withPlanCounts(syllabi.data, topics.data, lessons.data, progress.data);
  const levels = useMemo(
    () => [...new Set((classes.data?.results ?? []).map((item) => item.level).filter(Boolean))].sort((a, b) => levelRank(a) - levelRank(b) || a.localeCompare(b)),
    [classes.data],
  );
  const create = useMutation({
    mutationFn: (data: Partial<Syllabus>) => curriculumService.createSyllabus({ ...data, is_active: true }),
    onSuccess: (created: Syllabus) => {
      setCreating(false);
      refresh();
      setParams({ syllabus: String(created.id) });
    },
  });
  const open = (id: number | null) => {
    setNotice(null);
    setParams(id ? { syllabus: String(id) } : {});
  };
  // Counts and coverage come from four lists; until all have arrived, the
  // table would show "no topics" and "0%" that are not true.
  const counting = syllabi.isLoading || topics.isLoading || lessons.isLoading || progress.isLoading;
  const opened = openId && !counting ? rows?.find((row) => row.id === openId) : undefined;

  if (openId && opened) {
    return (
      <SyllabusWorkspace
        key={opened.id}
        syllabus={opened}
        topics={topics.data ?? []}
        lessons={lessons.data ?? []}
        progress={progress.data ?? []}
        subjects={subjects.data ?? []}
        levels={levels}
        canLead={canLead}
        onBack={() => open(null)}
        onChanged={refresh}
        onRemoved={(text) => {
          refresh();
          open(null);
          setNotice({ tone: "success", text });
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Syllabi</h1>
        <p className="mt-1 text-sm text-gray-500">
          What is taught in each subject at each class level: its topics in order, lesson plans, and how far each class has got.
        </p>
      </div>
      {openId && !counting && !opened && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">That syllabus could not be found. It may have been deleted.</p>
      )}
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="All syllabi"
        description={canLead ? "Open a syllabus to plan its topics and lessons. Syllabi you create cover every stream of a class level." : "Open a syllabus to read its plan. You can plan the ones for the subjects and classes you teach."}
        actions={canLead && (
          <button type="button" onClick={() => { create.reset(); setCreating(true); }} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New syllabus
          </button>
        )}
        rows={rows}
        config={syllabusConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => open(row.id)}
        openLabel={(row) => (row.can_edit ? "Plan" : "Open")}
        noun={["syllabus", "syllabi"]}
        exportName={{ file: "syllabi", title: "Syllabi" }}
        isLoading={counting}
        isError={syllabi.isError}
        onRetry={() => syllabi.refetch()}
        emptyText={canLead ? "No syllabi yet. Create one for each subject and class level." : "No syllabi have been set up yet."}
      />
      {creating && (
        <SyllabusForm
          subjects={subjects.data ?? []}
          levels={levels}
          busy={create.isPending}
          error={create.isError ? apiErrorMessage(create.error, "The syllabus could not be created.") : undefined}
          onSave={(data) => create.mutate(data)}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
